import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { useEdgeToEdgeLayout } from "PFApp/react_app_layout";
import { CarouselCard } from "PFComponents/carousel/carousel_card";
import Pill from "PFComponents/pill/pill";
import { editCurrentProfile } from "PFCore/services/current_profile";
import MaximiseIcon from "PFIcons/maximise";
import MinimiseIcon from "PFIcons/minimise";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DevelopSkills } from "./develop_skills";
import css from "./learning_and_development.less";

const LearningAndDevelopment = ({ fetchCourses, fetchRssFeed, isOneColumn }) => {
  useEdgeToEdgeLayout();
  const { currentProfile } = useContext(AppContext).store;
  const { t } = useTranslation("feed");

  // Here we fetch and render developmental skills, rss feed, courses and champions
  const [courses, setCourses] = useState([]);
  const [rssFeeds, setRssFeeds] = useState([]);
  const [mainView, setMainView] = useState(false);
  const [dismissDevSkills, setDismissDevSkills] = useState(false);
  const [developmentalSkillsList, setDevelopmentalSkills] = useState(
    (currentProfile.skills || []).filter((skill) => skill.developmental)
  );
  const [matchinCourses, setMatchinCourses] = useState(true);
  const [renderCourses, setRenderCourses] = useState(false);

  useEffect(() => {
    setDevelopmentalSkills((currentProfile.skills || []).filter((skill) => skill.developmental));
  }, [dismissDevSkills]);

  const courseTemplate = currentProfile.templates.find(({ key }) => key === "course");

  const hasDevelopmentalSkillsSet = developmentalSkillsList.length > 2;

  const handleSubmitDevelopmentalSkills = (skills) => {
    const selectedSkillsId = (skills || []).map((skill) => skill.id);
    const newSkills = [
      ...(currentProfile.skills || []).filter((skill) => !selectedSkillsId.includes(skill.id)),
      ...skills
    ];

    editCurrentProfile({ id: currentProfile.id, skills: newSkills });
  };

  const wrapperRef = useRef();

  useEffect(() => {
    if (courseTemplate && courseTemplate.read_permitted) {
      fetchCourses(matchinCourses).then((resp) => {
        setCourses(
          resp.entries.map((item) => ({
            title: item.name,
            description: item.description,
            imageURL: item.metadata && item.metadata.og && item.metadata.og.image,
            courseURL: item.metadata && item.metadata.og && item.metadata.og.url
          }))
        );
        setRenderCourses(true);
      });
    }
  }, [courseTemplate, matchinCourses]);

  useEffect(() => {
    // as there is only one carousel for rss all the feeds are merged
    fetchRssFeed().then((resp) => {
      const mergedRss = resp
        .reduce((accumulator, currentValue) => [...accumulator, ...currentValue.items], [])
        .sort((item1, item2) =>
          item2.published_at < item1.published_at ? -1 : item2.published_at > item1.published_at ? 1 : 0
        );

      setRssFeeds(mergedRss);
    });
  }, []);

  const isOneColumnCollapsed = isOneColumn && !mainView;
  const isOneColumnExpanded = isOneColumn && mainView;

  const columnWidth = isOneColumn ? "100%" : 400;

  return (
    <>
      <div
        className={classNames(css.learningDevelopment, {
          [css.mobileCollapsed]: isOneColumnCollapsed,
          [css.mobileExpanded]: isOneColumnExpanded
        })}
        style={{ width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth }}
        ref={wrapperRef}
      >
        <div className={classNames(css.wrapper, { [css.wrapperOneCollumn]: isOneColumn })}>
          <div
            role="button"
            tabIndex="0"
            className={classNames(css.title, { [css.titleOneColumn]: isOneColumn })}
            onClick={() => isOneColumn && setMainView(!mainView)}
            onKeyDown={() => isOneColumn && setMainView(!mainView)}
          >
            {t("learningAndDevelopment")}
            {isOneColumn && (
              <Pill
                style={{
                  backgroundColor: PF?.themeVars?.["--Palette-action"] || "#6ef29c",
                  height: 20,
                  margin: "0 10px",
                  padding: "0 12px"
                }}
              >
                {t("new")}
              </Pill>
            )}
            {isOneColumnCollapsed && <MaximiseIcon style={{ verticalAlign: "middle" }} />}
            {isOneColumnExpanded && <MinimiseIcon style={{ verticalAlign: "middle" }} />}
          </div>

          <div className={classNames(css.content, { [css.contentCollapsed]: isOneColumnCollapsed })}>
            {!dismissDevSkills && !hasDevelopmentalSkillsSet && (
              <DevelopSkills
                handleSubmit={handleSubmitDevelopmentalSkills}
                handleDismiss={setDismissDevSkills}
                style={{ marginBottom: 10 }}
                developmentalSkillsList={developmentalSkillsList}
              />
            )}

            {rssFeeds.length > 0 && (
              <CarouselCard
                itemsCollection={rssFeeds}
                kind="feed"
                title={t("newsAndInspiration")}
                style={{ marginBottom: 10 }}
              />
            )}

            {renderCourses && <hr />}
            {renderCourses && (
              <CarouselCard
                itemsCollection={courses}
                kind="course"
                title={t("coursesForYou")}
                style={{ marginBottom: 10 }}
                noItemsAction={() => setMatchinCourses(false)}
                noItemsLabel={
                  <div className={css.noItems}>
                    <span>{`${t("noMatchingCourses")} `}</span>
                    <span className={css.underlinedText}>{t("seeAllCourses")}</span>
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className={css.spacer} />
    </>
  );
};

LearningAndDevelopment.propTypes = {
  fetchCourses: PropTypes.func,
  fetchRssFeed: PropTypes.func,
  isOneColumn: PropTypes.bool
};

export default LearningAndDevelopment;
